'use client';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useRef, useState, useTransition } from 'react';
import { useTranslations } from 'next-intl';
import { Button, Card, FormError, Icon } from '@/components/site/ui';
import { Stepper } from '@/components/ui/stepper';
import { CheckboxField, Field, Input } from '@/components/ui/field';
import { DatePicker } from '@/components/ui/date-picker';
import { HintHover } from '@/components/ui/hint-hover';
import { Callout } from '@/components/ui/callout';
import { PhoneInput } from '@/components/ui/phone-input';
import { EmptyState } from '@/components/ui/empty-state';
import { BookableSlotGrid } from '@/components/app/bookable-slot-grid';
import { groupBookableSlots } from '@/lib/slots';
import { clinicSitePath } from '@/lib/websites';
import { fmtDate } from '@/lib/mappers';
import { servicesOf, type ClinicBookingPublic } from '@/lib/clinic-booking';
import { ChoiceRows } from '@/components/ui/choice-rows';
import { cn, formatMoney } from '@/lib/utils';
import { BookingProfessionalSummary } from './booking-professional-summary';
import { sendClinicBookingRequest } from './booking-actions';
import { BookingCodeStep } from './booking-code-step';
import { TurnstileWidget, turnstileSiteKey } from '@/components/ui/turnstile-widget';

type Step = 'service' | 'time' | 'contact' | 'code' | 'sent';
const STEP_ORDER: Step[] = ['service', 'time', 'contact', 'code', 'sent'];

export function ClinicBookingPage({
  slug,
  data,
  idempotencyKey,
  initialServiceId,
}: {
  slug: string;
  data: ClinicBookingPublic;
  idempotencyKey: string;
  initialServiceId?: string;
}) {
  const t = useTranslations('clinicBooking');
  const p = data.professional;
  const days = groupBookableSlots(
    data.slots.map((s) => ({ iso: s.startsAt, mode: 'Presencial' as const })),
    p.timezone,
    'pt',
  );
  const [date, setDate] = useState<string | null>(days[0]?.dateKey ?? null);
  const [selected, setSelected] = useState<string | null>(null);
  const services = servicesOf(p);
  // One service skips the choice; several start with it (owner, 2026-11-02).
  const [serviceId, setServiceId] = useState<string | null>(services.find((x) => x.id === initialServiceId)?.id ?? services[0]?.id ?? null);
  // The first service starts selected, so the summary card doesn't grow when the person picks one.
  const service = services.find((x) => x.id === serviceId);
  const [step, setStepState] = useState<Step>(services.length === 1 || services.some((x) => x.id === initialServiceId) ? 'time' : 'service');
  // Which way the step content slides in: forward from the right, back from the left.
  const direction = useRef<'forward' | 'back'>('forward');
  const setStep = (next: Step) => {
    direction.current = STEP_ORDER.indexOf(next) < STEP_ORDER.indexOf(step) ? 'back' : 'forward';
    setStepState(next);
  };
  const [email, setEmail] = useState('');
  const [patientName, setPatientName] = useState('');
  const [phone, setPhone] = useState('');
  const [consent, setConsent] = useState(false);
  const [error, setError] = useState('');
  const [receipt, setReceipt] = useState('');
  const router = useRouter();
  const [slotTaken, setSlotTaken] = useState(false);
  const [verification, setVerification] = useState<{ id: string; phoneEnding: string; resendAfter: string } | null>(null);
  const siteKey = turnstileSiteKey();
  const [captcha, setCaptcha] = useState<string | null>(null);
  const [pending, startTransition] = useTransition();
  const slot = data.slots.find((s) => s.startsAt === selected);
  const stepIds = [...(services.length > 1 ? ['service'] : []), 'time', 'contact'] as const;
  // The code belongs to the contact step: the stepper stays on "Seus dados".
  const stepIndex = Math.max(1, stepIds.indexOf((step === 'code' ? 'contact' : step) as (typeof stepIds)[number]) + 1);
  function submit(event: React.FormEvent) {
    event.preventDefault();
    if (!slot) return;
    setError('');
    startTransition(async () => {
      const result = await sendClinicBookingRequest(slug, p.id, slot.id, {
        idempotencyKey,
        patientName,
        phone,
        ...(email.trim() ? { email: email.trim() } : {}),
        ...(serviceId ? { serviceId } : {}),
        consent,
        consentVersion: data.consentVersion,
        ...(captcha ? { turnstileToken: captcha } : {}),
      });
      if (!result.data) {
        setError(t(result.error ?? 'failed'));
        return;
      }
      if (result.data.status === 'verify') {
        setVerification({ id: result.data.verificationId, phoneEnding: result.data.phoneEnding, resendAfter: result.data.resendAfter });
        setStep('code');
        return;
      }
      setReceipt(result.data.id);
      setStep('sent');
    });
  }
  return (
    <main className="mx-auto max-w-6xl space-y-6 px-4 py-6 md:px-8 md:py-10">
      <header className="flex flex-wrap items-center justify-between gap-3 border-b border-border pb-5">
        <Link href={clinicSitePath(slug)} className="text-lg font-semibold">
          {data.clinicName}
        </Link>
        <Link href={`${clinicSitePath(slug)}/agendar`} className="text-sm text-muted-foreground underline">
          {t('backProfessionals')}
        </Link>
      </header>
      <div className="grid items-start gap-6 lg:grid-cols-[320px_minmax(0,1fr)]">
        <Card className="p-4 md:p-7">
          <BookingProfessionalSummary professional={p} service={service} compact />
        </Card>
        <Card className="space-y-6 overflow-hidden p-4 md:p-7" aria-busy={pending}>
          {step === 'sent' ? (
            // Success screen (owner, 2026-11-02): a clear mark, what was asked for, what happens next.
            <div className="flex flex-col items-center gap-5 py-6 text-center" role="status">
              <span className="flex h-16 w-16 items-center justify-center rounded-full bg-success-soft text-success-soft-foreground">
                <Icon name="calendar-check-in-01" size={30} />
              </span>
              <div className="space-y-2">
                <h1 className="text-2xl font-semibold">{t('sentTitle')}</h1>
                <p className="mx-auto max-w-md text-muted-foreground">{t('sentBody')}</p>
              </div>
              {slot && (
                <div className="w-full max-w-sm rounded-control border border-border p-4 text-left text-sm">
                  <p className="font-semibold text-foreground">{p.name}</p>
                  <p className="text-muted-foreground">{service?.name}</p>
                  <p className="mt-2 flex items-center gap-2 text-foreground">
                    <Icon name="clock-01" size={16} className="text-medlux" />
                    {fmtDate(new Date(slot.startsAt), { dateStyle: 'long', timeStyle: 'short' }, p.timezone, 'pt')}
                  </p>
                </div>
              )}
              <p className="break-all text-xs text-muted-foreground">{t('receipt', { id: receipt })}</p>
              <Button asChild variant="outline">
                <Link href={clinicSitePath(slug)}>{t('backSite')}</Link>
              </Button>
            </div>
          ) : (
            <>
              <div className="space-y-2">
                <Stepper
                  ariaLabel={t('stepsLabel')}
                  srPosition={t('stepOf', { current: stepIndex, total: stepIds.length })}
                  current={stepIndex}
                  steps={stepIds.map((id) => ({ id, label: t(`step_${id}`) }))}
                  className="mb-4 max-w-sm"
                />
                <div key={`h-${step}`} className={cn('flex flex-wrap items-center justify-between gap-2', `booking-step-${direction.current}`)}>
                  <div className="flex items-center gap-1.5">
                    <h1 className="text-xl font-semibold tracking-tight sm:text-2xl">
                      {t(step === 'service' ? 'chooseService' : step === 'time' ? 'chooseTime' : step === 'code' ? 'codeTitle' : 'yourContact')}
                    </h1>
                    {step === 'time' && <HintHover label={t('timezoneHint', { timezone: p.timezone })} />}
                  </div>
                  {step === 'time' && services.length > 1 && (
                    <Button
                      variant="unstyled"
                      onClick={() => setStep('service')}
                      className="ml-auto text-sm font-semibold text-medlux underline decoration-dotted decoration-2 underline-offset-[6px]"
                    >
                      {t('changeService')}
                    </Button>
                  )}
                </div>
                {/* Only at the last step, right before the person sends the request (owner, 2026-11-02). */}
                {step === 'contact' && <p className="text-sm leading-relaxed text-muted-foreground">{t('requestHint')}</p>}
              </div>
              <div key={step} className={`booking-step-${direction.current}`}>
              {step === 'service' ? (
                <div className="space-y-5">
                  <ChoiceRows
                    aria-label={t('chooseService')}
                    value={serviceId ?? ''}
                    onChange={setServiceId}
                    options={services.map((x) => ({
                      value: x.id,
                      label: x.name,
                      hint: t('duration', { minutes: x.durationMinutes }),
                      trailing: formatMoney(x.priceCents, 'BRL', 'pt-BR'),
                    }))}
                  />
                  <Button block disabled={!service} onClick={() => setStep('time')}>
                    {t('continue')}
                  </Button>
                </div>
              ) : step === 'time' ? (
                <div className="space-y-5">
                  {slotTaken && (
                    <Callout tone="warning" icon="alert-02">
                      {t('slotTaken')}
                    </Callout>
                  )}
                  {days.length ? (
                    <>
                      <div className="grid gap-6 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] lg:grid-cols-[260px_minmax(0,1fr)]">
                        <DatePicker
                          inline
                          id="booking-date"
                          aria-label={t('date')}
                          availableDates={days.map((d) => d.dateKey)}
                          value={date}
                          mode="any"
                          hideNavigation
                          onChange={(value) => {
                            setDate(value);
                            setSelected(null);
                          }}
                          className="w-full"
                        />
                        <div className="space-y-4">
                          <BookableSlotGrid
                            days={days.filter((d) => d.dateKey === date)}
                            selected={selected}
                            onSelect={setSelected}
                            tz={p.timezone}
                            slotLayout="grid3"
                          />
                          {!days.some((d) => d.dateKey === date) && (
                            <p className="text-sm text-muted-foreground">{t('emptyDay')}</p>
                          )}
                        </div>
                      </div>
                      <Button block disabled={!slot} onClick={() => setStep('contact')}>
                        {t('continue')}
                      </Button>
                    </>
                  ) : (
                    <EmptyState icon="calendar-03" title={t('emptySlots')}>
                      <p>{t('emptySlotsBody')}</p>
                    </EmptyState>
                  )}
                </div>
              ) : step === 'code' && verification ? (
                <BookingCodeStep
                  slug={slug}
                  verificationId={verification.id}
                  phoneEnding={verification.phoneEnding}
                  resendAfter={verification.resendAfter}
                  onConfirmed={(id) => {
                    setReceipt(id);
                    setStep('sent');
                  }}
                  onChangePhone={() => setStep('contact')}
                  onSlotTaken={() => {
                    setSlotTaken(true);
                    setSelected(null);
                    setStep('time');
                    router.refresh();
                  }}
                />
              ) : (
                <form className="space-y-5" onSubmit={submit}>
                  {slot && (
                    // What is being requested, at a glance: day, time and service, each with its icon.
                    <dl className="grid gap-3 rounded-control bg-medlux-strong p-4 text-sm text-white sm:grid-cols-3">
                      {[
                        { icon: 'calendar-03', label: t('date'), value: fmtDate(new Date(slot.startsAt), { weekday: 'short', day: 'numeric', month: 'long' }, p.timezone, 'pt') },
                        { icon: 'clock-01', label: t('time'), value: fmtDate(new Date(slot.startsAt), { hour: '2-digit', minute: '2-digit' }, p.timezone, 'pt') },
                        {
                          icon: 'stethoscope',
                          label: t('step_service'),
                          value: `${service?.name ?? ''} · ${t('duration', { minutes: service?.durationMinutes ?? Math.round((Date.parse(slot.endsAt) - Date.parse(slot.startsAt)) / 60000) })}`,
                        },
                      ].map((row) => (
                        <div key={row.icon} className="flex items-center gap-3">
                          <Icon name={row.icon} size={22} className="shrink-0 text-white" />
                          <div className="min-w-0">
                            <dt className="text-xs text-white/70">{row.label}</dt>
                            <dd className="font-semibold text-white">{row.value}</dd>
                          </div>
                        </div>
                      ))}
                    </dl>
                  )}
                  <Field label={t('patientName')} htmlFor="booking-name">
                    <Input
                      id="booking-name"
                      autoComplete="name"
                      required
                      minLength={2}
                      maxLength={120}
                      value={patientName}
                      onChange={(e) => setPatientName(e.target.value)}
                      disabled={pending}
                    />
                  </Field>
                  <Field label={t('phone')} htmlFor="booking-phone">
                    <PhoneInput
                      id="booking-phone"
                      value={phone}
                      onChange={setPhone}
                      disabled={pending}
                      required
                    />
                  </Field>
                  <Field label={t('email')} htmlFor="booking-email" hint={t('emailHint')}>
                    <Input
                      id="booking-email"
                      type="email"
                      autoComplete="email"
                      maxLength={254}
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      disabled={pending}
                    />
                  </Field>
                  <p className="text-xs leading-relaxed text-muted-foreground">
                    {t('privacy', { clinic: data.clinicName })}
                  </p>
                  <CheckboxField
                    required
                    checked={consent}
                    onChange={(e) => setConsent(e.target.checked)}
                    disabled={pending}
                    label={t('consent')}
                  />
                  {siteKey && <TurnstileWidget siteKey={siteKey} onToken={setCaptcha} />}
                  <FormError>{error}</FormError>
                  <div className="flex flex-wrap gap-3">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setStep('time')}
                      disabled={pending}
                    >
                      {t('back')}
                    </Button>
                    <Button
                      type="submit"
                      loading={pending}
                      disabled={!consent || !phone || patientName.trim().length < 2 || Boolean(siteKey && !captcha)}
                    >
                      {t('sendRequest')}
                    </Button>
                  </div>
                </form>
              )}
              </div>
            </>
          )}
        </Card>
      </div>
    </main>
  );
}
